import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Tooltip,
  Typography,
  IconButton
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarningIcon from '@mui/icons-material/Warning';
import CampaignTableRow from './CampaignTableRow';
import { extractDomainIdentifier } from './utils/campaignGrouping';

/**
 * Component for a campaign group row with expandable child campaigns
 * Shows aggregated metrics for the group with expand/collapse functionality
 */
const CampaignGroupRow = ({
  group,
  startIndex,
  isSubid3View,
  getTaboolaCampaignData,
  isTaboolaCampaign,
  getFacebookCampaignData,
  isFacebookCampaign,
  handleDrillDown,
  openBudgetDialog,
  toggleCampaignStatus,
  handleBlacklist,
  loading,
  isBlacklisted,
  selectedSubid2,
  facebookCampaigns,
  channelWarnings,
  defaultExpanded = false
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Sync with defaultExpanded prop when it changes
  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    setExpanded(prev => !prev);
  }, []);

  const { title, campaigns, aggregated } = group;

  // Get unique domain identifiers for display
  const domains = [...new Set(campaigns.map(c => extractDomainIdentifier(c.campaign_name)))];
  const domainDisplay = domains.length > 2
    ? `${domains.slice(0, 2).join(', ')} +${domains.length - 2}`
    : domains.join(', ');

  // Check if any child campaigns have channel warnings or no-revenue warnings
  const groupWarnings = useMemo(() => {
    let missingCount = 0;
    let duplicateCount = 0;
    let noRevenueCount = 0;
    const duplicateChannels = new Set();

    campaigns.forEach(campaign => {
      const domain = extractDomainIdentifier(campaign.campaign_name);
      const cost = parseFloat(campaign.cost || 0);
      const revenue = parseFloat(campaign.revenue || 0);

      // Check for no revenue with cost > $5
      if (cost > 5 && revenue === 0) {
        noRevenueCount++;
      }

      // Check channel warnings only if channelWarnings data is available
      if (channelWarnings) {
        const channelId = channelWarnings.campaign_channels?.[campaign.campaign_id];
        const isKnowledgia = domain?.toLowerCase() === 'knowledgia';

        if (isKnowledgia && !channelId) {
          missingCount++;
        }
        if (channelId && channelWarnings.duplicate_channels?.[channelId]) {
          duplicateCount++;
          duplicateChannels.add(channelId);
        }
      }
    });

    if (missingCount === 0 && duplicateCount === 0 && noRevenueCount === 0) return null;

    return { missingCount, duplicateCount, noRevenueCount, duplicateChannels: [...duplicateChannels] };
  }, [campaigns, channelWarnings]);

  return (
    <>
      {/* Group Header Row */}
      <TableRow
        sx={{
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
            cursor: 'pointer'
          },
          transition: 'background-color 0.2s'
        }}
        onClick={handleToggle}
      >
        <TableCell
          sx={{
            width: '25%',
            pr: 0.5,
            display: isSubid3View ? 'none' : 'table-cell'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <IconButton
              size="small"
              onClick={handleToggle}
              sx={{
                padding: '2px',
                mr: 0.5,
                width: 20,
                height: 20
              }}
            >
              {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Tooltip
                title={`${campaigns.length} campaigns: ${domainDisplay}`}
                enterDelay={500}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: 'primary.main',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {title}
                </Typography>
              </Tooltip>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  color: 'text.secondary',
                  display: 'block'
                }}
              >
                {campaigns.length} campaigns ({domainDisplay})
              </Typography>
            </Box>
            {/* Channel and revenue warnings for group */}
            {groupWarnings && (
              <Tooltip
                title={
                  <>
                    {groupWarnings.missingCount > 0 && (
                      <div>{groupWarnings.missingCount} knowledgia campaign{groupWarnings.missingCount > 1 ? 's' : ''} missing channel ID</div>
                    )}
                    {groupWarnings.duplicateCount > 0 && (
                      <div>{groupWarnings.duplicateCount} campaign{groupWarnings.duplicateCount > 1 ? 's' : ''} with duplicate channel{groupWarnings.duplicateChannels.length > 1 ? 's' : ''}</div>
                    )}
                    {groupWarnings.noRevenueCount > 0 && (
                      <div>{groupWarnings.noRevenueCount} campaign{groupWarnings.noRevenueCount > 1 ? 's' : ''} with no revenue (cost &gt; $5)</div>
                    )}
                  </>
                }
              >
                <WarningIcon
                  sx={{
                    fontSize: '1rem',
                    color: groupWarnings.noRevenueCount > 0 ? '#9c27b0' : (groupWarnings.missingCount > 0 ? '#ed6c02' : '#d32f2f'),
                    ml: 0.5,
                    flexShrink: 0
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </TableCell>

        {/* Clicks - hidden on mobile */}
        <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Tooltip title="Total Bought Clicks for group">
            <span style={{ fontWeight: 600 }}>
              {aggregated.clicks.toLocaleString()}
            </span>
          </Tooltip>
        </TableCell>

        {/* CTR - hidden on mobile */}
        <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Tooltip title="Average CTR for group">
            <span style={{ fontWeight: 600 }}>
              {aggregated.ctr.toFixed(2)}%
            </span>
          </Tooltip>
        </TableCell>

        {/* CVR - hidden on mobile */}
        <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Tooltip title="Conversion Rate for group">
            <span style={{ fontWeight: 600 }}>
              {aggregated.cvr.toFixed(2)}%
            </span>
          </Tooltip>
        </TableCell>

        {/* Conversions - hidden on mobile */}
        <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Tooltip title="Total Conversions for group">
            <span style={{ fontWeight: 600 }}>
              {aggregated.demand_clicks.toLocaleString()}
            </span>
          </Tooltip>
        </TableCell>

        {/* Cost - visible on mobile */}
        <TableCell align="right" className="number-cell">
          <span style={{ fontWeight: 600 }}>
            ${aggregated.cost.toFixed(2)}
          </span>
        </TableCell>

        {/* Revenue - visible on mobile */}
        <TableCell align="right" className="number-cell">
          <span style={{ fontWeight: 600 }}>
            ${aggregated.revenue.toFixed(2)}
          </span>
        </TableCell>

        {/* EPC (Payout) - hidden on mobile */}
        <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Tooltip title="Earnings Per Conversion for group">
            <span style={{ fontWeight: 600 }}>
              ${aggregated.payout.toFixed(2)}
            </span>
          </Tooltip>
        </TableCell>

        {/* CPC - hidden on mobile */}
        <TableCell align="right" className="number-cell" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Tooltip title="Cost Per Click for group">
            <span style={{ fontWeight: 600 }}>
              ${aggregated.cpc.toFixed(2)}
            </span>
          </Tooltip>
        </TableCell>

        {/* ROAS - visible on mobile */}
        <TableCell
          align="right"
          className="number-cell"
          sx={{
            color: aggregated.roas >= 1 ? 'success.main' : 'error.main'
          }}
        >
          <Tooltip title="Return on Ad Spend for group">
            <span style={{ fontWeight: 600 }}>
              {aggregated.roas.toFixed(2)}
            </span>
          </Tooltip>
        </TableCell>

        {/* Profit - hidden on mobile */}
        <TableCell
          align="right"
          className="number-cell"
          sx={{
            color: aggregated.profit >= 0 ? 'success.main' : 'error.main',
            display: { xs: 'none', md: 'table-cell' }
          }}
        >
          <span style={{ fontWeight: 600 }}>
            ${aggregated.profit.toFixed(2)}
          </span>
        </TableCell>

        {/* Budget - empty for group row, hidden on mobile */}
        <TableCell
          align="right"
          className="number-cell"
          sx={{ display: isSubid3View ? 'none' : { xs: 'none', md: 'table-cell' } }}
        >
          <Typography variant="caption" color="text.secondary">-</Typography>
        </TableCell>

        {/* Blacklist column (subid3 view only) */}
        <TableCell sx={{ display: isSubid3View ? 'table-cell' : 'none' }} />

        {/* Actions - empty for group row */}
        <TableCell sx={{ display: isSubid3View ? 'none' : 'table-cell' }} />
      </TableRow>

      {/* Child Campaign Rows - shown when expanded */}
      {expanded && campaigns.map((campaign, index) => (
        <CampaignTableRow
          key={`${group.groupId}-child-${campaign.campaign_id}`}
          campaign={campaign}
          index={startIndex + index}
          isSubid3View={isSubid3View}
          selectedSubid2={selectedSubid2}
          getTaboolaCampaignData={getTaboolaCampaignData}
          isTaboolaCampaign={isTaboolaCampaign}
          getFacebookCampaignData={getFacebookCampaignData}
          isFacebookCampaign={isFacebookCampaign}
          handleDrillDown={handleDrillDown}
          openBudgetDialog={openBudgetDialog}
          toggleCampaignStatus={toggleCampaignStatus}
          handleBlacklist={handleBlacklist}
          loading={loading}
          isBlacklisted={isBlacklisted}
          facebookCampaigns={facebookCampaigns}
          channelWarnings={channelWarnings}
          isChildRow={true}
        />
      ))}
    </>
  );
};

export default CampaignGroupRow;
